import axios from "axios";
import authStore from "../stores/AuthStore";

class LogAPIHandler {
  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_LOG_API_URL || "http://localhost:8001",
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });

    this.api.interceptors.request.use((config) => {
      const currentUser = authStore.getUser();
      const userID = currentUser?.id || currentUser?.userID;
      if (userID) {
        config.headers["X-User-ID"] = userID;
        if (config.method !== "get") {
          config.data = { userID, ...config.data };
        }
      }
      return config;
    });
  }

  /**
   * Fetch logs for all devices (with pagination)
   * @param {number} page - Page number (starts at 1)
   * @param {number} limit - Number of logs per page
   */
  async fetchAllLogs({ page = 1, limit = 100 } = {}) {
    try {
      const response = await this.api.get("/logs/recent", {
        params: { page, limit },
      });
      return response.data;
    } catch (error) {
      console.error("❌ Error fetching all logs:", error);
      throw error;
    }
  }

  /**
   * Fetch logs for a specific device (with pagination)
   * @param {string} deviceId - Device ID to filter
   * @param {number} page - Page number
   * @param {number} limit - Number of logs per page
   */
  async fetchLogsByDevice({ deviceId, page = 1, limit = 100 }) {
    if (!deviceId) throw new Error("Device ID is required");

    try {
      const response = await this.api.get(`/logs/device/${deviceId}`, {
        params: { page, limit },
      });
      return response.data;
    } catch (error) {
      console.error(`❌ Error fetching logs for device ${deviceId}:`, error);
      throw error;
    }
  }

  /**
   * Fetch latest logs (optionally for a specific device)
   * @param {string} [deviceId] - Optional device ID
   * @param {number} [limit=100] - Number of logs
   */
  async fetchLatestLogs({ deviceId = null, limit = 100 } = {}) {
    if (deviceId) {
      const response = await this.api.get(`/logs/device/${deviceId}`, {
        params: { limit },
      });
      return response.data;
    } else {
      const response = await this.api.get(`/logs/recent`, {
        params: { limit },
      });
      return response.data;
    }
  }

  /**
   * Export logs as JSON, CSV, or HTML file
   * @param {'json'|'csv' | 'html'} format - Format to export
   * @param {Object} options - Options for export
   * @param {string} [options.deviceId]
   * @param {string} [options.start]
   * @param {string} [options.end]
   * @param {boolean} [options.compress=false]
   * @param {string} [options.filename] - Optional filename (no extension)
   */
  async exportLogs(
    format = "csv",
    {
      deviceIds = [],
      start,
      end,
      compress = false,
      filename = `logs_export_${Date.now()}`,
      groupByDevice = false,
    } = {}
  ) {
    try {
      const endpointObj = {
        csv: "/logs/export/csv",
        json: "/logs/export/json",
        html: "/logs/export/html",
      };

      const endpoint = endpointObj[format] || "/logs/export/csv";

      const requestBody = {
        device_ids: deviceIds,
        start,
        end,
        compress,
        filename,
        group_logs_by_device: groupByDevice,
      };

      const response = await this.api.post(endpoint, requestBody, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      let extension = format;
      if (compress) extension += ".gz";

      link.download = `${filename}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("❌ Error exporting logs:", error);
      throw error;
    }
  }
}

const logAPIHandler = new LogAPIHandler();
export default logAPIHandler;
